import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  Divider,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  School as SchoolIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  AutoStories as AutoStoriesIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as EmojiEventsIcon,
  Login as LoginIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

interface InquiryFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  districtName: string;
  role: string;
  studentCount: string;
  message: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [openInquiryDialog, setOpenInquiryDialog] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [formData, setFormData] = useState<InquiryFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    districtName: '',
    role: '',
    studentCount: '',
    message: '',
  });

  const handleGetStarted = () => {
    setOpenInquiryDialog(true);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleCloseDialog = () => {
    setOpenInquiryDialog(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      districtName: '',
      role: '',
      studentCount: '',
      message: '',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitInquiry = () => {
    // In a real application, this would send the data to your backend
    console.log('District Inquiry Submitted:', formData);

    // Show success message
    setShowSuccessMessage(true);
    handleCloseDialog();

    // Here you would typically make an API call to save the inquiry
    // Example: await fetch('/api/inquiries', { method: 'POST', body: JSON.stringify(formData) });
  };

  const isFormValid = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.districtName.trim() !== '' &&
      formData.role.trim() !== ''
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              color: '#667eea',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            Koshmi
          </Typography>
          {!isAuthenticated ? (
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={handleLogin}
              sx={{
                bgcolor: '#667eea',
                color: 'white',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  bgcolor: '#5568d3',
                },
              }}
            >
              Login
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard')}
              sx={{
                bgcolor: '#667eea',
                color: 'white',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  bgcolor: '#5568d3',
                },
              }}
            >
              Dashboard
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Spacer for fixed AppBar */}
      <Toolbar />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              AI driven Education Intervention platform for K-12
            </Typography>

            <Typography
              variant="h5"
              sx={{
                mb: 4,
                fontWeight: 400,
                opacity: 0.95,
                fontSize: { xs: '1.1rem', md: '1.5rem' },
              }}
            >
              Our Vision: To build a scalable, responsible AI platform that helps schools and
              districts improve learning outcomes
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mb: 4, flexWrap: 'wrap' }}
            >
              <Chip
                label="Grades K-12"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 2.5,
                }}
              />
              <Chip
                label="SBAC-Aligned"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 2.5,
                }}
              />
              <Chip
                label="Teacher-in-the-Loop AI"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 2.5,
                }}
              />
            </Stack>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                maxWidth: '800px',
                mx: 'auto',
                opacity: 0.9,
              }}
            >
              Built with the mission to provide outcome-based results to underperforming schools and
              districts
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  bgcolor: 'white',
                  color: '#667eea',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: '#f0f0f0',
                  },
                }}
              >
                Request a District Pilot
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                View Teacher Workflow
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* The Problem Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, mb: 4, color: '#333' }}
        >
          The Problem
        </Typography>

        <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#fff' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3, color: '#667eea' }}>
            Schools and districts lack Critical Intervention Window
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
            Districts are expected to improve SBAC outcomes, but teachers face:
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <GroupsIcon sx={{ color: '#667eea', mr: 2, fontSize: 30 }} />
                <Typography variant="body1">Wide skill variance in classrooms</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <AssessmentIcon sx={{ color: '#667eea', mr: 2, fontSize: 30 }} />
                <Typography variant="body1">
                  Limited time to create differentiated materials
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <SchoolIcon sx={{ color: '#667eea', mr: 2, fontSize: 30 }} />
                <Typography variant="body1">
                  Tools that are static and poorly aligned to assessments
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              borderLeft: '4px solid #667eea',
              pl: 3,
              py: 2,
              bgcolor: '#f8f9ff',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontStyle: 'italic', color: '#333', fontWeight: 500 }}>
              Districts are accountable for outcomes — but teachers lack scalable intervention
              tools.
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* The Solution Section */}
      <Box sx={{ bgcolor: '#f8f9ff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 2, color: '#333' }}
          >
            Purpose-Built SBAC Intervention Platform
          </Typography>

          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 6, maxWidth: '900px', mx: 'auto', color: '#555', lineHeight: 1.8 }}
          >
            Koshmi enables teachers to leverage AI to generate SBAC-aligned Math and ELA content in
            minutes and deliver adaptive practice that targets each student's mastery gaps.
          </Typography>

          {/* Value Pillars */}
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <PsychologyIcon sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Standards-Aligned AI Content
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically aligned to grade, standard, and difficulty.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <CheckCircleIcon sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Teacher-Controlled Workflows
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    AI assists — teachers decide.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <TrendingUpIcon sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Student-Level Mastery Tracking
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Growth measured by standard, not just scores.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <EmojiEventsIcon sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Designed for Underperforming Schools
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Built for intervention, not enrichment-only use.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Teacher Workflow Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          sx={{
            borderLeft: '4px solid #667eea',
            pl: 3,
            mb: 6,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#333' }}>
            Built for Teachers, Not to Replace Them
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9ff' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#667eea' }}>
                  1. Select grade, standard, and skill focus
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9ff' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#667eea' }}>
                  2. AI generates aligned questions and explanations
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9ff' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#667eea' }}>
                  3. Teacher reviews and adjusts content
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9ff' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#667eea' }}>
                  4. Students practice
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9ff' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#667eea' }}>
                  5. Dashboard tracks mastery by standard
                </Typography>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 4,
                bgcolor: '#667eea',
                color: 'white',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ fontStyle: 'italic', fontWeight: 500, mb: 2 }}>
                Teachers remain in full control — AI reduces prep time, not professional judgment.
              </Typography>
              <AutoStoriesIcon sx={{ fontSize: 80, opacity: 0.8, mt: 2 }} />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Student Experience Section */}
      <Box sx={{ bgcolor: '#f8f9ff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, color: '#333' }}
          >
            Personalized Practice That Builds Confidence
          </Typography>

          <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
            <Typography
              variant="h6"
              align="center"
              sx={{ mb: 4, fontStyle: 'italic', color: '#555', lineHeight: 1.8 }}
            >
              Students receive targeted practice based on their specific gaps, with clear
              explanations and feedback designed to reduce test anxiety and improve understanding.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Standards-based progression
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Immediate feedback
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <EmojiEventsIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Focus on mastery, not memorization
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* SBAC Alignment Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, mb: 4, color: '#333' }}
        >
          Designed for SBAC Readiness and Measurable Growth
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', p: 3 }}>
              <AssessmentIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                SBAC-style question formats
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', p: 3 }}>
              <TrendingUpIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Difficulty bands aligned to assessment rigor
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%', p: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 50, color: '#667eea', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Pre- and post-intervention tracking
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Use Cases Section */}
      <Box sx={{ bgcolor: '#f8f9ff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 6, color: '#333' }}
          >
            How Schools Use Koshmi
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600, color: '#667eea', mb: 2 }}
                  >
                    Middle School Math Intervention
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Close foundational gaps in grades 6–8.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600, color: '#667eea', mb: 2 }}
                  >
                    ELA Skill Building
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Support reading comprehension and writing practice.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600, color: '#667eea', mb: 2 }}
                  >
                    After-School & Enrichment Programs
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Same platform, same data — inside and outside the classroom.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Equity & Impact Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ fontWeight: 700, mb: 4, color: '#333' }}
        >
          Focused on Where Support Is Needed Most
        </Typography>

        <Paper elevation={3} sx={{ p: 5, bgcolor: '#fff', textAlign: 'center' }}>
          <Typography
            variant="h6"
            sx={{ lineHeight: 1.8, color: '#555', maxWidth: '800px', mx: 'auto' }}
          >
            Koshmi is designed for schools facing accountability pressure and limited instructional
            resources, helping teachers deliver personalized learning without additional burden.
          </Typography>
        </Paper>
      </Container>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
              Ready to Transform Learning Outcomes?
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  bgcolor: 'white',
                  color: '#667eea',
                  fontWeight: 600,
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: '#f0f0f0',
                  },
                }}
              >
                Request a District Pilot
              </Button>

              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 600,
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Investor Inquiries
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#333', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center">
            © 2024 Koshmi. All rights reserved. Built with the mission to improve K-12 learning
            outcomes.
          </Typography>
        </Container>
      </Box>

      {/* District Inquiry Dialog */}
      <Dialog
        open={openInquiryDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#667eea',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Request a District Pilot
          </Typography>
          <Button onClick={handleCloseDialog} sx={{ color: 'white', minWidth: 'auto', p: 0.5 }}>
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, color: '#555' }}>
            Thank you for your interest in Koshmi! Please fill out the form below and our team will
            contact you within 24-48 hours to discuss how we can support your district's learning
            outcomes.
          </Typography>

          <Stack spacing={2.5}>
            <TextField
              fullWidth
              required
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              variant="outlined"
              placeholder="(555) 123-4567"
            />

            <TextField
              fullWidth
              required
              label="District/School Name"
              name="districtName"
              value={formData.districtName}
              onChange={handleInputChange}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              select
              label="Your Role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              variant="outlined"
            >
              <MenuItem value="superintendent">Superintendent</MenuItem>
              <MenuItem value="principal">Principal</MenuItem>
              <MenuItem value="assistant-principal">Assistant Principal</MenuItem>
              <MenuItem value="curriculum-director">Curriculum Director</MenuItem>
              <MenuItem value="teacher">Teacher</MenuItem>
              <MenuItem value="instructional-coach">Instructional Coach</MenuItem>
              <MenuItem value="technology-director">Technology Director</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              fullWidth
              select
              label="Approximate Student Count"
              name="studentCount"
              value={formData.studentCount}
              onChange={handleInputChange}
              variant="outlined"
            >
              <MenuItem value="under-500">Under 500</MenuItem>
              <MenuItem value="500-1000">500 - 1,000</MenuItem>
              <MenuItem value="1000-2500">1,000 - 2,500</MenuItem>
              <MenuItem value="2500-5000">2,500 - 5,000</MenuItem>
              <MenuItem value="5000-10000">5,000 - 10,000</MenuItem>
              <MenuItem value="over-10000">Over 10,000</MenuItem>
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Tell us about your needs (optional)"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              variant="outlined"
              placeholder="What challenges are you facing? What are your goals for student outcomes?"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitInquiry}
            disabled={!isFormValid()}
            startIcon={<SendIcon />}
            sx={{
              bgcolor: '#667eea',
              color: 'white',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                bgcolor: '#5568d3',
              },
              '&:disabled': {
                bgcolor: '#ccc',
              },
            }}
          >
            Submit Inquiry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%', fontSize: '1rem' }}
        >
          Thank you for your inquiry! Our team will contact you within 24-48 hours.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home;
